import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import Debounce from '@scripts/utilities/debounce.js';
import { ASYNC_STATUS, isLoading } from '@scripts/utilities/redux-helpers.js';
import RestClient from '@scripts/utilities/rest-client.js';

import { getUser } from './user.js';

const VERSION = 'v2';

const initialState = {
  status: ASYNC_STATUS.IDLE,
  /**
   * A list of the assets in a member's portfolio
   */
  assets: [],
  selectedPortfolio: {},
  isOpenPortfolioCustom: false,
};


var fetchPortfolio = createAsyncThunk('portfolio/fetch', function() {
  return RestClient.get(`/wp-json/sbm/${VERSION}/dashboard/portfolio/`);
});


/**
 * Save the user's portfolio
 */
var persistPortfolio = Debounce(function(state) {
    var user = getUser(state);

    if (user && user.activeMember) {
      var portfolio = getPortfolioState(state);
      RestClient.post(`/wp-json/sbm/${VERSION}/dashboard/portfolio/`, portfolio);
    }
}, 1000);


const portfolioSlice = createSlice({
    name: 'portfolio',
    initialState: initialState,
    reducers: {
      /**
       * a special-case to bypass the fetch step. Only to be used for
       * non-logged in users
       */
      bypassFetch: function(state, action) {
        state.status = ASYNC_STATUS.COMPLETE;
      },
      setAssets: function(state, action) {
        state.assets = action.payload;
      },
      setSelectedPortfolio: function(state, action) {
        state.selectedPortfolio = action.payload;
      },
      setIsOpenPortfolioCustom: function(state, action) {
        state.isOpenPortfolioCustom = action.payload;
      }
    },
    extraReducers: function(builder) {
      builder
        .addCase(fetchPortfolio.pending, function(state) {
          state.status = ASYNC_STATUS.INPROGRESS;
        })
        .addCase(fetchPortfolio.fulfilled, function(state, action) {
          state.assets = action.payload.assets;
          state.status = ASYNC_STATUS.COMPLETE;
        });
    }
});

export function getIsOpenPortfolioCustom(state) {
  return !!getPortfolioState(state).isOpenPortfolioCustom;
}

/**
 * helper function for retrieving the portfolio from the store
 */
function getPortfolioState(state) {
  return state.portfolio;
}

export function isPortfolioLoading(state) {
  return isLoading(getPortfolioState(state).status);
}
export function getPortfolioAssets(state) {
  return getPortfolioState(state).assets;
}
export function getSelectedPortfolio(state) {
  return getPortfolioState(state).selectedPortfolio;
}
export function getAssetQuantityById(state, assetId, portfolioId) {
  let result = 0;
  const assets = getPortfolioAssets(state);
  for (const element of assets) {
    if (element.asset == assetId && element.portfolioId == portfolioId) {
      result = element.quantity;
    }
  }

  return result;
}


/**
 * Action creators
 */
export var fetch = function() {
  return function(dispatch, getState) {
    var state = getState();
    var user = getUser(state);

    if (user && user.activeMember) {
      var portfolio = getPortfolioState(state);

      if (portfolio.status == ASYNC_STATUS.IDLE) {
        dispatch(fetchPortfolio());
      }
    } else {
      dispatch(portfolioSlice.actions.bypassFetch());
    }
  };
};

export const setIsOpenPortfolioCustom = function(isOpen) { 
  return function(dispatch) {
    dispatch(portfolioSlice.actions.setIsOpenPortfolioCustom(isOpen));
  };
}

export const setSelectedPortfolio = function(portfolio) { 
  return function(dispatch) {
    dispatch(portfolioSlice.actions.setSelectedPortfolio(portfolio));
  };
}

export const setAssetQuantity = function(assetId, quantity, pId) {
  if (quantity < 0) {
    return;
  }

  return function(dispatch, getState) {
    const assets = getPortfolioAssets(getState());

    const result = [...assets];
    const assetIdx = result.findIndex(({asset, portfolioId}) => asset === assetId && portfolioId === pId);
    if(assetIdx > -1) {
      result[assetIdx] = {...result[assetIdx], quantity, portfolioId: pId} ;
    } else {
      result.push({
        asset: assetId, quantity, portfolioId: pId
      });
    }

    dispatch(portfolioSlice.actions.setAssets(result));

    persistPortfolio(getState());
  };
};

/**
 * when a member removes an asset from the dashboard, ensure we zero-out
 * the portfolio amount.
 */
export var syncPortfolioToDashboard = function(dashboardAssets) {
  return function(dispatch, getState) {
    var portfolio = getPortfolioAssets(getState());

    for (var i = 0; i < portfolio.length; i++) {
      var inDashboard = false;
      for (var j = 0; j < dashboardAssets.length; j++) {
        if (dashboardAssets[j] == portfolio[i].asset) {
          inDashboard = true;
          break;
        }
      }

      if (! inDashboard) {
        dispatch(setAssetQuantity(portfolio[i].asset, 0));
      }
    }
  };
};

export const deletePortfolioAssets = function(portfolioId) {
  return function(dispatch, getState) {
    const assets = getPortfolioAssets(getState());

    const result = assets.filter((asset) => asset.portfolioId !== portfolioId);
    dispatch(portfolioSlice.actions.setAssets(result));
  
    persistPortfolio(getState());
  };
    
};

export const deletePortfolioAssetsByIds = function(assetsIds, portfolioId) {
  return function(dispatch, getState) {
    const assets = getPortfolioAssets(getState());
    // Filter assets not removed from portfolio
    const result = assets.filter((asset) => !(assetsIds.includes(asset.asset) && asset.portfolioId === portfolioId));
    dispatch(portfolioSlice.actions.setAssets(result));
  
    persistPortfolio(getState());
  };
};

export default portfolioSlice.reducer;

